/**
 * Shared door trigger utility — used by ZonesDevices and OperationalView.
 *
 * Routing:
 *   Guarita        → guaritaOpenDoor (cloud via integration ID)
 *   Hikvision LPR  → Electron: direct ISAPI PUT barrierGateCtrl
 *                    Cloud fallback: iot-connect /hikvision-lpr/:cameraId/open-door
 *   Alphadigi LPR  → iot-connect /alphadigi-cameras/:cameraId/open-door (pending_open heartbeat)
 *   ControlID/Hikvision (face) in Electron with IP → DeviceCommunicationService HYBRID
 *     HYBRID: probe local (400ms) → probe public (1.5s) → direct → cloud fallback
 *   Everything else → cloud REST endpoint
 *     RFID  → /rfid/devices/:id/gate/open
 *     Face  → /facial-recognition/devices/:id/door/open
 */

import { deviceCommunicationService, CommunicationMode } from '../services/deviceCommunication';
import { guaritaOpenDoor } from '../services/guaritaIntegrationService';
import { isElectron } from './platform';

export interface TriggerableDevice {
  id: string;
  name?: string;
  manufacturer?: string;
  device_type?: string;
  ip_address?: string;
  local_ip_address?: string;
  port?: number;
  username?: string;
  password?: string;
  config?: Record<string, any>;
}

type FetchFn = (url: string, init?: RequestInit) => Promise<Response>;

// ── vendor helpers ────────────────────────────────────────────────────────────

export function isControlIDDevice(device: Pick<TriggerableDevice, 'manufacturer' | 'device_type'>): boolean {
  const mfr = device.manufacturer?.toLowerCase() ?? '';
  return (
    // Substring match, not exact equality — ControlID's iDBox/iDFace lines report
    // their manufacturer as "idbox"/"idface", not literally "controlid". Mirrors
    // the same vendor-detection convention used on the iot-connect backend.
    mfr.includes('controlid') ||
    mfr.includes('control id') ||
    mfr.includes('idface') ||
    mfr.includes('idbox') ||
    device.device_type === 'controlid_face' ||
    device.device_type === 'controlid_rfid'
  );
}

export function isHikvisionDevice(device: Pick<TriggerableDevice, 'manufacturer'>): boolean {
  return device.manufacturer?.toLowerCase() === 'hikvision';
}

export function isRFIDDevice(device: Pick<TriggerableDevice, 'device_type'>): boolean {
  return device.device_type === 'rfid_reader' || device.device_type === 'controlid_rfid';
}

// ── main function ─────────────────────────────────────────────────────────────

/**
 * Trigger a door/gate open for any supported device type.
 * Throws on failure — callers should wrap in try/catch and show the message.
 */
export async function triggerDeviceDoor(
  device: TriggerableDevice,
  fetchWithAuth: FetchFn,
): Promise<void> {
  const isGuarita = device.device_type === 'guarita';
  const isRFID = isRFIDDevice(device);
  const isControlID = isControlIDDevice(device);
  const isHikvision = isHikvisionDevice(device);
  const inElectron = isElectron();
  const hasIp = !!(device.local_ip_address || device.ip_address);
  const supportsDirectComm = isControlID || isHikvision;

  // ── Guarita ────────────────────────────────────────────────────────────────
  if (isGuarita) {
    const integrationId = device.config?.guarita_integration_id;
    if (!integrationId) throw new Error('Dispositivo Guarita sem integration_id configurado');
    await guaritaOpenDoor(
      integrationId,
      device.config?.guarita_receptor ?? 1,
      device.config?.guarita_saida ?? 1,
    );
    return;
  }

  // ── Hikvision LPR — barrier gate ───────────────────────────────────────────
  if (device.device_type === 'hikvision_lpr' || (device.device_type === 'lpr' && isHikvision)) {
    const localIp = device.local_ip_address || device.ip_address;
    if (inElectron && localIp) {
      // Direct ISAPI call from Electron — DS-TCG406 barrier control
      // Try multiple endpoints in order — DS-TCG406 firmware varies
      const auth = { username: device.username || 'admin', password: device.password || '' };
      const hikNS = 'xmlns="http://www.hikvision.com/ver20/XMLSchema"';
      const attempts = [
        // 1. Traffic VehicleDetect barrierControl — DS-TCG406 native LPR endpoint
        {
          url: `${localIp}/ISAPI/Traffic/channels/1/VehicleDetect/barrierControl`,
          method: 'PUT' as const,
          body: `<?xml version="1.0" encoding="UTF-8"?><barrierGateCtrl ${hikNS}><barrierStatus>open</barrierStatus></barrierGateCtrl>`,
        },
        // 2. Traffic BarrierGate ctrl — alternate firmware path
        {
          url: `${localIp}/ISAPI/Traffic/BarrierGate/1/ctrl`,
          method: 'PUT' as const,
          body: `<?xml version="1.0" encoding="UTF-8"?><BarrierGate ${hikNS}><barrierGateStatus>open</barrierGateStatus></BarrierGate>`,
        },
        // 3. Traffic channels barrier ctrl — another common path
        {
          url: `${localIp}/ISAPI/Traffic/channels/1/barrier/ctrl`,
          method: 'PUT' as const,
          body: `<?xml version="1.0" encoding="UTF-8"?><barrierGateCtrl ${hikNS}><barrierStatus>open</barrierStatus></barrierGateCtrl>`,
        },
        // 4. AccessControl RemoteControl door/0 (0-indexed on some TCG firmware)
        {
          url: `${localIp}/ISAPI/AccessControl/RemoteControl/door/0`,
          method: 'PUT' as const,
          body: `<?xml version="1.0" encoding="UTF-8"?><RemoteControlDoor version="2.0" ${hikNS}><cmd>open</cmd></RemoteControlDoor>`,
        },
        // 5. IO output relay trigger (if barrier connected to wiegand/IO relay)
        {
          url: `${localIp}/ISAPI/System/IO/outputs/1/trigger`,
          method: 'PUT' as const,
          body: `<?xml version="1.0" encoding="UTF-8"?><IOPortData version="2.0" ${hikNS}><outputState>high</outputState></IOPortData>`,
        },
      ];
      let result: any = { success: false };
      for (const attempt of attempts) {
        result = await (window as any).electronAPI.deviceHttpRequest({
          url: attempt.url,
          method: attempt.method,
          headers: { 'Content-Type': 'application/xml' },
          body: attempt.body,
          timeout: 5000,
          digestAuth: auth,
        });
        console.log(`[LPR barrier] ${attempt.method} ${attempt.url} → ${result.statusCode} success=${result.success} error=${result.error}`);
        if (result.success) break;
      }
      if (!result.success) throw new Error(result.error || `Falha ao acionar barreira (HTTP ${result.statusCode ?? 'N/A'})`);
      return;
    }
    // Cloud fallback: iot-connect opens barrier via stored ISAPI credentials
    const cameraId = device.config?.hikvision_lpr_camera_id;
    if (!cameraId) throw new Error('Câmera LPR Hikvision não vinculada a este dispositivo');
    const res = await fetchWithAuth(`/api/v1/iot-connect/hikvision-lpr/${cameraId}/open-door`, { method: 'POST' });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error((body as any).error || `Falha ao acionar barreira (HTTP ${res.status})`);
    }
    return;
  }

  // ── Alphadigi LPR — pending_open via next heartbeat ───────────────────────
  if (device.device_type === 'alphadigi_lpr' || (device.device_type === 'lpr' && device.manufacturer?.toLowerCase() === 'alphadigi')) {
    const cameraId = device.config?.alphadigi_camera_id;
    if (!cameraId) throw new Error('Câmera LPR Alphadigi não vinculada a este dispositivo');
    const res = await fetchWithAuth(`/api/v1/iot-connect/alphadigi-cameras/${cameraId}/open-door`, { method: 'POST' });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error((body as any).error || `Falha ao acionar barreira (HTTP ${res.status})`);
    }
    return;
  }

  // ── Electron direct/hybrid ─────────────────────────────────────────────────
  if (inElectron && hasIp && supportsDirectComm) {
    const result = await deviceCommunicationService.openDoor(
      {
        id: device.id,
        ip_address: device.local_ip_address || device.ip_address || '',
        local_ip_address: device.local_ip_address,
        username: device.username || '',
        password: device.password || '',
        vendor: isControlID ? 'controlid' : 'hikvision',
        port: device.port?.toString(),
        device_type: device.device_type,
        config: device.config,
      },
      CommunicationMode.HYBRID,
    );

    if (!result.success) {
      throw new Error(result.error || 'Falha ao acionar (sem resposta do dispositivo)');
    }

    // Fire-and-forget audit log for face devices (RFID has no face log endpoint)
    if (!isRFID) {
      fetchWithAuth(`/api/v1/iot-connect/facial-recognition/devices/${device.id}/door/open`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ log_only: true, gate_id: 1 }),
      }).catch(err => console.warn('[triggerDeviceDoor] Audit log failed (non-blocking):', err));
    }
    return;
  }

  // ── Cloud fallback ─────────────────────────────────────────────────────────
  const endpoint = isRFID
    ? `/api/v1/iot-connect/rfid/devices/${device.id}/gate/open`
    : `/api/v1/iot-connect/facial-recognition/devices/${device.id}/door/open`;

  // RFID/UHF terminals (iDUHF, iDBox) use the configurable output/door number
  // (controlid_door_number) instead of a fixed gate — mirrors openControlIDDoor's
  // direct-comm path in deviceCommunication.ts.
  const gateId = isRFID ? (device.config?.controlid_door_number ?? 1) : 1;

  const response = await fetchWithAuth(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ gate_id: gateId }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Falha ao acionar (HTTP ${response.status})`);
  }
}
